// Privacy Policy page rendered at /privacy. Public — no auth required.
// Content kept in sync with Downloads/privacy-policy.md.
import { useEffect } from 'react'

export default function PrivacyPolicy({ onNavigate }) {
  // Document title is overwritten everywhere else; restore on unmount.
  useEffect(() => {
    const prev = document.title
    document.title = 'Privacy Policy · The Bible Community'
    return () => { document.title = prev }
  }, [])

  function goBack(e) {
    e?.preventDefault?.()
    if (onNavigate) onNavigate('/')
    else if (typeof window !== 'undefined' && window.history?.length > 1) window.history.back()
  }

  return (
    <div style={S.shell}>
      <style>{CSS}</style>
      <div style={S.bar}>
        <a href="/" onClick={goBack} style={S.back}>← Back</a>
        <div style={S.brand}>The Bible Community</div>
      </div>
      <article style={S.article}>
        <h1 style={S.h1}>Privacy Policy</h1>
        <p style={S.meta}><strong>The Bible Community</strong><br/>Last updated: 5 November 2026</p>
        <p style={S.lead}>
          This Privacy Policy describes how Sonrise Studios Ltd ("we", "us", or "our"), which runs The Bible Community, collects,
          uses, and protects your personal information when you use The Bible Community app and
          website (collectively, the "Service").
        </p>

        <hr style={S.hr}/>

        <h2 style={S.h2}>1. Who We Are</h2>
        <p style={S.p}>Sonrise Studios Ltd is a company registered in England and Wales (company number 15999485). We operate The Bible Community, a platform that connects church congregations around Bible reading, sermon notes, and community features.</p>
        <p style={S.p}><strong>Contact:</strong><br/>
          <a href="mailto:hello@thebiblecommunity.co.uk" style={S.a}>hello@thebiblecommunity.co.uk</a><br/>
          <a href="https://thebiblecommunity.co.uk" style={S.a}>thebiblecommunity.co.uk</a>
        </p>

        <hr style={S.hr}/>

        <h2 style={S.h2}>2. Information We Collect</h2>
        <h3 style={S.h3}>Information you provide to us</h3>
        <p style={S.p}><strong>Account information</strong></p>
        <ul style={S.ul}>
          <li>Email address (required to create an account)</li>
          <li>First name (optional, can be added or changed at any time)</li>
          <li>Profile photo (optional, can be added or changed at any time)</li>
        </ul>
        <p style={S.p}><strong>Church information</strong></p>
        <ul style={S.ul}>
          <li>Your home church (optional, required to access church-specific features)</li>
          <li>Additional churches you choose to follow for sermon notes (optional)</li>
        </ul>
        <p style={S.p}><strong>Content you create</strong></p>
        <ul style={S.ul}>
          <li>Verse comments and reflections you post</li>
          <li>Prayer requests you share</li>
          <li>Community board posts</li>
          <li>Encouragements you submit</li>
          <li>Saved verses and highlights</li>
        </ul>

        <h3 style={S.h3}>Information collected automatically</h3>
        <p style={S.p}><strong>Usage data</strong></p>
        <ul style={S.ul}>
          <li>Pages and features you interact with within the app</li>
          <li>Device type and operating system</li>
          <li>App version</li>
        </ul>
        <p style={S.p}><strong>Authentication data</strong></p>
        <ul style={S.ul}>
          <li>We use Supabase for authentication. When you sign in with email, a one-time code is generated. When you sign in with Google, we receive basic profile information (name, email, profile photo) from Google.</li>
        </ul>

        <h3 style={S.h3}>Information we do not collect</h3>
        <ul style={S.ul}>
          <li>We do not collect your home address</li>
          <li>We do not collect your phone number</li>
          <li>We do not collect payment information directly (handled by Stripe if applicable)</li>
          <li>We do not use advertising tracking or third-party analytics</li>
          <li>We do not sell your data to any third party</li>
        </ul>

        <hr style={S.hr}/>

        <h2 style={S.h2}>3. How We Use Your Information</h2>
        <p style={S.p}>We use the information we collect to:</p>
        <ul style={S.ul}>
          <li>Create and manage your account</li>
          <li>Show you sermon notes and Bible content from your church</li>
          <li>Display your name and photo to other members of your church community</li>
          <li>Send you one-time sign-in codes and account-related emails</li>
          <li>Send church invite emails when a church administrator invites you</li>
          <li>Show your prayer requests, posts, and comments to your church community based on your sharing preferences</li>
          <li>Improve and maintain the Service</li>
        </ul>

        <hr style={S.hr}/>

        <h2 style={S.h2}>4. How We Share Your Information</h2>
        <h3 style={S.h3}>Within your church community</h3>
        <p style={S.p}>Content you post (comments, prayers, encouragements, community posts) is visible to other members of your church based on the visibility settings you choose:</p>
        <ul style={S.ul}>
          <li><strong>Public to church</strong> — visible to all members of your church</li>
          <li><strong>Elders only</strong> — visible only to church elders and administrators</li>
          <li><strong>Private</strong> — visible only to you</li>
        </ul>
        <p style={S.p}>Your name and profile photo are visible to other members of your church.</p>

        <h3 style={S.h3}>Service providers</h3>
        <p style={S.p}>We use the following third-party services to operate the platform:</p>
        <ul style={S.ul}>
          <li><strong>Supabase</strong> — database and authentication (supabase.com)</li>
          <li><strong>Netlify</strong> — website hosting (netlify.com)</li>
          <li><strong>Resend</strong> — transactional email delivery (resend.com)</li>
          <li><strong>Google OAuth</strong> — optional sign-in (accounts.google.com)</li>
          <li><strong>YouTube</strong> — sermon video links (we link to YouTube videos but do not host them)</li>
          <li><strong>Anthropic Claude</strong> — AI processing of sermon transcripts to extract Bible references (no personal data is sent to this service)</li>
          <li><strong>ScrapeCreators</strong> — fetching YouTube transcripts for sermon processing (no personal data is sent to this service)</li>
        </ul>
        <p style={S.p}>These providers are only given access to information necessary to perform their functions and are prohibited from using it for other purposes.</p>

        <h3 style={S.h3}>Legal requirements</h3>
        <p style={S.p}>We may disclose your information if required to do so by law or in response to valid legal requests from public authorities.</p>

        <h3 style={S.h3}>Business transfers</h3>
        <p style={S.p}>If Sonrise Studios Ltd is acquired or merges with another company, your information may be transferred as part of that transaction. We will notify you before your information is transferred and becomes subject to a different privacy policy.</p>

        <hr style={S.hr}/>

        <h2 style={S.h2}>5. Data Storage and Security</h2>
        <p style={S.p}>Your data is stored securely on Supabase servers located in the European Union (eu-west-2 region).</p>
        <p style={S.p}>We implement appropriate technical and organisational measures to protect your personal information including:</p>
        <ul style={S.ul}>
          <li>Encrypted connections (HTTPS) for all data in transit</li>
          <li>Row-level security on our database — you can only access data you are authorised to see</li>
          <li>Role-based access control — church members, elders, and administrators have different levels of access</li>
          <li>Secure authentication via one-time codes or Google OAuth</li>
        </ul>
        <p style={S.p}>No method of transmission or storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.</p>

        <hr style={S.hr}/>

        <h2 style={S.h2}>6. Data Retention</h2>
        <p style={S.p}>We retain your personal information for as long as your account is active or as needed to provide the Service.</p>
        <p style={S.p}>If you delete your account:</p>
        <ul style={S.ul}>
          <li>Your profile information is deleted immediately upon request</li>
          <li>Content you have posted (comments, prayers, posts) is anonymised or deleted</li>
          <li>Some information may be retained for legal compliance purposes</li>
        </ul>

        <hr style={S.hr}/>

        <h2 style={S.h2}>7. Your Rights</h2>
        <p style={S.p}>Under UK GDPR and the Data Protection Act 2018, you have the following rights:</p>
        <p style={S.p}><strong>Right to access</strong> — You can request a copy of the personal data we hold about you.</p>
        <p style={S.p}><strong>Right to rectification</strong> — You can correct inaccurate personal data at any time from your profile settings.</p>
        <p style={S.p}><strong>Right to erasure</strong> — You can delete your account at any time at <a href="/delete-account" style={S.a}>thebiblecommunity.co.uk/delete-account</a> or from your profile settings in the app. If you can't sign in, email <a href="mailto:hello@thebiblecommunity.co.uk" style={S.a}>hello@thebiblecommunity.co.uk</a> and we'll delete your account within 30 days.</p>
        <p style={S.p}><strong>Right to portability</strong> — You can request your personal data in a portable format.</p>
        <p style={S.p}><strong>Right to object</strong> — You can object to processing of your personal data in certain circumstances.</p>
        <p style={S.p}><strong>Right to restrict processing</strong> — You can request that we limit how we use your data.</p>
        <p style={S.p}>To exercise any of these rights, contact us at <a href="mailto:hello@thebiblecommunity.co.uk" style={S.a}>hello@thebiblecommunity.co.uk</a>. Account deletion takes effect immediately when requested in-app; other rights requests we will respond to as soon as possible.</p>
        <p style={S.p}>You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noreferrer" style={S.a}>ico.org.uk</a>.</p>

        <hr style={S.hr}/>

        <h2 style={S.h2}>8. Children's Privacy</h2>
        <p style={S.p}>The Bible Community is not directed at children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe a child under 13 has provided us with personal information, please contact us at <a href="mailto:hello@thebiblecommunity.co.uk" style={S.a}>hello@thebiblecommunity.co.uk</a> and we will delete that information.</p>
        <p style={S.p}>For users between 13 and 17, parental consent may be required depending on your jurisdiction.</p>

        <hr style={S.hr}/>

        <h2 style={S.h2}>9. Cookies and Local Storage</h2>
        <p style={S.p}>The Bible Community app uses browser local storage (not traditional cookies) to:</p>
        <ul style={S.ul}>
          <li>Keep you signed in between sessions</li>
          <li>Remember your reading position in the Bible</li>
          <li>Cache Bible verses and sermon content for faster loading</li>
          <li>Store your church and display preferences</li>
        </ul>
        <p style={S.p}>We do not use advertising cookies or third-party tracking cookies.</p>

        <hr style={S.hr}/>

        <h2 style={S.h2}>10. Push Notifications</h2>
        <p style={S.p}>If you grant permission for push notifications, we may send you notifications about:</p>
        <ul style={S.ul}>
          <li>Replies to your prayer requests or comments</li>
          <li>New community posts from your church</li>
          <li>Church announcements from administrators</li>
        </ul>
        <p style={S.p}>You can disable push notifications at any time in your device settings.</p>

        <hr style={S.hr}/>

        <h2 style={S.h2}>11. Third-Party Links</h2>
        <p style={S.p}>The Service includes links to YouTube videos. When you click these links, you leave our Service and are subject to YouTube's privacy policy (<a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" style={S.a}>policies.google.com/privacy</a>). We are not responsible for the privacy practices of third-party sites.</p>

        <hr style={S.hr}/>

        <h2 style={S.h2}>12. International Users</h2>
        <p style={S.p}>The Bible Community is operated from the United Kingdom. If you access the Service from outside the UK, your information may be transferred to and processed in the UK. By using the Service, you consent to this transfer.</p>

        <hr style={S.hr}/>

        <h2 style={S.h2}>13. Changes to This Policy</h2>
        <p style={S.p}>We may update this Privacy Policy from time to time. We will notify you of significant changes by:</p>
        <ul style={S.ul}>
          <li>Sending an email to your registered address</li>
          <li>Displaying a notice in the app</li>
        </ul>
        <p style={S.p}>Your continued use of the Service after changes are posted constitutes your acceptance of the updated policy. The date at the top of this page indicates when it was last updated.</p>

        <hr style={S.hr}/>

        <h2 style={S.h2}>14. Contact Us</h2>
        <p style={S.p}>If you have any questions about this Privacy Policy or how we handle your personal data, please contact us:</p>
        <p style={S.p}>
          <strong>Sonrise Studios Ltd</strong> (The Bible Community)<br/>
          Registered in England and Wales, company number 15999485<br/>
          Registered office: Whiteoaks, Vicarage Road, Tunbridge Wells, England, TN4 0SN<br/>
          Email: <a href="mailto:hello@thebiblecommunity.co.uk" style={S.a}>hello@thebiblecommunity.co.uk</a><br/>
          Website: <a href="https://thebiblecommunity.co.uk" style={S.a}>thebiblecommunity.co.uk</a>
        </p>
        <p style={S.p}>We are registered with the Information Commissioner's Office (ICO) in the United Kingdom.</p>

        <div style={S.foot}>
          <a href="/" onClick={goBack} style={S.a}>← Back to The Bible Community</a>
        </div>
      </article>
    </div>
  )
}

const S = {
  shell:   { minHeight: '100vh', background: 'var(--cream)', color: 'var(--ink)', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" },
  bar:     { position: 'sticky', top: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', borderBottom: '1px solid var(--border)', background: 'rgba(246,242,236,0.92)', backdropFilter: 'blur(8px)' },
  back:    { color: 'var(--gold-ink)', fontSize: 13, textDecoration: 'none', fontWeight: 700, letterSpacing: '0.04em' },
  brand:   { fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--gold-ink)' },
  article: { maxWidth: 760, margin: '0 auto', padding: '40px 24px 80px' },
  h1:      { fontFamily: "'Source Serif 4', Georgia, serif", fontSize: 'clamp(32px, 6vw, 48px)', lineHeight: 1.1, marginBottom: 12 },
  h2:      { fontSize: 22, fontWeight: 700, marginTop: 32, marginBottom: 12, color: 'var(--ink)' },
  h3:      { fontSize: 17, fontWeight: 700, marginTop: 22, marginBottom: 10, color: 'var(--ink)' },
  meta:    { fontSize: 13, color: 'var(--stone)', marginBottom: 20, lineHeight: 1.6 },
  lead:    { fontSize: 16, lineHeight: 1.7, color: 'var(--ink)' },
  p:       { fontSize: 15, lineHeight: 1.7, color: 'var(--ink)', marginBottom: 12 },
  ul:      { fontSize: 15, lineHeight: 1.8, color: 'var(--ink)', marginBottom: 12, paddingLeft: 24 },
  a:       { color: 'var(--gold-ink)', textDecoration: 'underline' },
  hr:      { border: 'none', borderTop: '1px solid var(--border)', margin: '28px 0' },
  foot:    { marginTop: 40, paddingTop: 24, borderTop: '1px solid var(--border)', fontSize: 13 },
}

// Element-level overrides for nested content where inline styles can't reach
// (li > strong, etc.) and a small print-friendly fallback.
const CSS = `
  body { background: var(--cream); }
  @media print {
    .privacy-bar { display: none; }
    body { background: #fff; color: #000; }
  }
`
